import { useCallback, useState } from 'react';

import {
  getOtherTextField,
  supportsEditableOther,
} from '@/features/advisor-onboarding/questionnaire/lib/editable-other';

import type { Question, QuestionnaireAnswers } from '@shared';

interface UseSingleSelectQuestionOptions {
  question: Question;
  initialValue: string | null;
  initialOtherText: string;
  onSubmit: (patch: Partial<QuestionnaireAnswers>) => Promise<void> | void;
}

export interface SingleSelectQuestionState {
  value: string | null;
  setValue: (next: string) => void;
  otherText: string;
  setOtherText: (next: string) => void;
  hasEditableOther: boolean;
  isValid: boolean;
  submit: () => Promise<void>;
}

export function useSingleSelectQuestion({
  question,
  initialValue,
  initialOtherText,
  onSubmit,
}: UseSingleSelectQuestionOptions): SingleSelectQuestionState {
  const [value, setValue] = useState<string | null>(initialValue);
  const [otherText, setOtherText] = useState<string>(initialOtherText);

  const hasEditableOther = supportsEditableOther(question.id);
  const otherKey = getOtherTextField(question.id);

  const otherSelectedNeedsText = hasEditableOther && value === 'other' && !otherText.trim();
  const isValid = !!value && !otherSelectedNeedsText;

  const submit = useCallback(async () => {
    if (!isValid || !value) return;

    const patch: Partial<QuestionnaireAnswers> = {
      [question.id]: value,
    } as Partial<QuestionnaireAnswers>;

    if (hasEditableOther && otherKey) {
      (patch as Record<string, unknown>)[otherKey] =
        value === 'other' ? otherText.trim() : undefined;
    }

    await onSubmit(patch);
  }, [isValid, value, question.id, hasEditableOther, otherKey, otherText, onSubmit]);

  return { value, setValue, otherText, setOtherText, hasEditableOther, isValid, submit };
}
